import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import {
  register as apiRegister,
  resendVerificationEmail as apiResendVerification,
} from '../api/auth.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';

/**
 * Tenant registration — Dispatch Deck rebuild (epic #897 PR 8, formerly
 * the console-shared `createRegister` factory instantiation).
 *
 * Wire behavior preserved: email + password (10–128, hint in the label)
 * + optional workspace name; `POST /v1/auth/register`; success replaces
 * the form in place with a check-your-inbox card that offers a resend
 * button and a back-to-login link.
 *
 * Errors are single-channel (epic AC 7): submit and resend failures
 * render inline only. The resend acknowledgement stays an inline status
 * line (it belongs to the card, not a transient toast).
 */
export function Register(): JSX.Element {
  const { status, fetchImpl } = useSession();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendAck, setResendAck] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  if (status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0 || password.length === 0) {
      setError(t('register.error.empty_fields'));
      return;
    }
    if (password.length < 10) {
      setError(t('register.error.password_too_short'));
      return;
    }
    setSubmitting(true);
    try {
      await apiRegister(
        {
          email: trimmedEmail,
          password,
          ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
        },
        fetchImpl,
      );
      setSuccess(trimmedEmail);
      setResendAck(false);
      setResendError(null);
      setEmail('');
      setPassword('');
      setDisplayName('');
    } catch (err) {
      setError(coerceErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (success === null) {
      return;
    }
    setResendError(null);
    setResendSubmitting(true);
    try {
      await apiResendVerification({ email: success }, fetchImpl);
      setResendAck(true);
    } catch (err) {
      setResendError(coerceErrorMessage(err));
    } finally {
      setResendSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      title={t('register.subtitle')}
      description={t('register.description')}
      testId="register-page-frame"
      footer={
        <Link to="/login" data-testid="register-login-link" className={AUTH_LINK_CLASS}>
          {t('register.already_have_account')}
        </Link>
      }
    >
      {success !== null ? (
        <div
          className="flex flex-col gap-3 rounded-lg border border-ok-line bg-raised p-5"
          data-testid="register-success"
        >
          <h2 className="m-0 flex items-center gap-2 text-section text-paper">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-ok" />
            {t('register.success.title')}
          </h2>
          <p className="m-0 text-body text-paper-mid">
            {t('register.success.body', { email: success })}
          </p>
          {resendAck ? (
            <p className="m-0 text-dense text-paper-low" data-testid="register-resend-ack" role="status">
              {t('register.success.resend_ack')}
            </p>
          ) : (
            <p className="m-0 text-dense text-paper-low">{t('register.success.resend_hint')}</p>
          )}
          {resendError !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="register-resend-error">
              {resendError}
            </p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={resendSubmitting}
              onClick={() => {
                void handleResend();
              }}
              data-testid="register-resend-submit"
            >
              {resendSubmitting ? t('register.resend.submitting') : t('register.resend.submit')}
            </Button>
            <Link to="/login" data-testid="register-success-login" className={AUTH_LINK_CLASS}>
              {t('common.back_to_login')}
            </Link>
          </div>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('register.email')} fieldId="register-email">
            <Input
              id="register-email"
              type="email"
              mono
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="register-email"
            />
          </FormField>
          <FormField label={t('register.password_with_hint')} fieldId="register-password">
            <Input
              id="register-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              minLength={10}
              maxLength={128}
              data-testid="register-password"
            />
          </FormField>
          <FormField label={t('register.workspace_name')} fieldId="register-display-name">
            <Input
              id="register-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={submitting}
              maxLength={80}
              placeholder={t('register.workspace_placeholder')}
              data-testid="register-display-name"
            />
          </FormField>
          {error !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="register-error">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="register-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting ? t('register.submitting') : t('register.submit')}
          </Button>
        </form>
      )}
    </AuthPageFrame>
  );
}
